class CeCategoryDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.ce-category-drawer__overlay');
    this.dialog = this.querySelector('.ce-category-drawer__dialog');
    this.track = this.querySelector('[data-panel-track]');
    this.titleEl = this.querySelector('[data-drawer-title]');
    this.backButton = this.querySelector('[data-action="back-panel"]');
    this.rootTitle = this.titleEl?.textContent?.trim() || '';
    this.openTrigger = null;
    this.panelStack = ['root'];
    this.isOpen = false;
    this.desktopMedia = window.matchMedia('(min-width: 990px)');

    this.onKeyUp = this.onKeyUp.bind(this);
    this.onPanelClick = this.onPanelClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  get isDesktop() {
    return this.desktopMedia.matches;
  }

  connectedCallback() {
    this.addEventListener('click', this.onPanelClick);
    this.querySelectorAll('[data-action="close-drawer"]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.backButton?.addEventListener('click', () => this.goBack());

    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.isOpen && this.openTrigger === trigger) {
          this.close();
        } else {
          this.open(trigger);
        }
      });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onPanelClick);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get panels() {
    return Array.from(this.querySelectorAll('.ce-category-drawer__panel-view'));
  }

  get activePanelId() {
    return this.panelStack[this.panelStack.length - 1];
  }

  getPanel(panelId) {
    return this.querySelector(`[data-panel-id="${panelId}"]`);
  }

  open(trigger) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.openTrigger = trigger;
    this.panelStack = ['root'];
    this.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');

    if (!this.isDesktop) {
      document.body.classList.add('overflow-hidden-mobile');
    } else {
      window.setTimeout(() => {
        document.addEventListener('click', this.onDocumentClick);
      }, 0);
    }

    document.addEventListener('keyup', this.onKeyUp);
    this.updatePanels();

    if (!this.isDesktop) {
      trapFocus(this.dialog);
    }
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-open', 'is-subpanel');
    this.openTrigger?.setAttribute('aria-expanded', 'false');
    this.openTrigger = null;
    this.panelStack = ['root'];
    if (this.titleEl) {
      this.titleEl.textContent = this.rootTitle;
    }
    document.body.classList.remove('overflow-hidden-mobile');
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onDocumentClick);
    this.updatePanels();
    removeTrapFocus(this.openTrigger);
  }

  onDocumentClick(event) {
    if (!this.isOpen || !this.isDesktop) return;
    if (this.contains(event.target) || this.openTrigger?.contains(event.target)) return;
    this.close();
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      if (this.panelStack.length > 1) {
        this.goBack();
      } else {
        this.close();
      }
    }
  }

  onPanelClick(event) {
    const targetButton = event.target.closest('[data-panel-target]');
    if (!targetButton || !this.contains(targetButton)) return;

    const panelId = targetButton.dataset.panelTarget;
    if (!panelId || !this.getPanel(panelId)) return;

    this.panelStack.push(panelId);
    this.updatePanels();
    if (!this.isDesktop) {
      trapFocus(this.dialog);
    }
  }

  goBack() {
    if (this.panelStack.length <= 1) return;

    this.panelStack.pop();
    const activePanel = this.getPanel(this.activePanelId);
    const panelTitle = activePanel?.dataset.panelTitle;
    if (panelTitle) {
      this.titleEl.textContent = panelTitle;
    }
    this.updatePanels();
    if (!this.isDesktop) {
      trapFocus(this.dialog);
    }
  }

  updatePanels() {
    const depth = this.panelStack.length - 1;

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.panelId === this.activePanelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.classList.toggle('is-subpanel', this.isDesktop && depth > 0);

    if (this.backButton) {
      this.backButton.hidden = depth === 0;
    }

    const activePanel = this.getPanel(this.activePanelId);
    if (activePanel && this.titleEl) {
      this.titleEl.textContent = activePanel.dataset.panelTitle || this.rootTitle;
    }
  }
}

customElements.define('ce-category-drawer', CeCategoryDrawer);
